import { TranscriptionSegment } from '@/hooks/useTranscription';

/**
 * 格式化時間為 SRT 格式 (00:00:00,000)
 */
function formatSRTTime(seconds: number): string {
  const totalMs = Math.max(0, Math.round(seconds * 1000));
  const hours = Math.floor(totalMs / 3600000);
  const minutes = Math.floor((totalMs % 3600000) / 60000);
  const secs = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${ms.toString().padStart(3, '0')}`;
}

/**
 * 格式化時間為 VTT 格式 (00:00:00.000)
 */
function formatVTTTime(seconds: number): string {
  const totalMs = Math.max(0, Math.round(seconds * 1000));
  const hours = Math.floor(totalMs / 3600000);
  const minutes = Math.floor((totalMs % 3600000) / 60000);
  const secs = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;
  
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
}

/**
 * 生成 SRT 字幕格式
 * @param segments 轉錄片段
 * @returns SRT 字幕字符串
 */
export function generateSRT(segments: TranscriptionSegment[]): string {
  return segments.map((segment) => {
    return `${segment.id}\n${formatSRTTime(segment.start)} --> ${formatSRTTime(segment.end)}\n${segment.text}\n`;
  }).join('\n');
}

/**
 * 生成 VTT 字幕格式
 * @param segments 轉錄片段
 * @returns VTT 字幕字符串
 */
export function generateVTT(segments: TranscriptionSegment[]): string {
  const header = 'WEBVTT\n\n';
  const body = segments.map((segment) => {
    return `${formatVTTTime(segment.start)} --> ${formatVTTTime(segment.end)}\n${segment.text}\n`;
  }).join('\n');
  
  return header + body;
}

/**
 * 下載字幕文件
 * @param content 字幕內容
 * @param filename 文件名（不含擴展名）
 * @param format 格式 ('srt' | 'vtt')
 */
export function downloadSubtitle(
  content: string, 
  filename: string, 
  format: 'srt' | 'vtt'
): void {
  const mimeType = format === 'srt' ? 'text/plain' : 'text/vtt';
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.${format}`;
  a.click();

  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * 快捷方法：生成並下載 SRT 字幕
 */
export function downloadSRT(segments: TranscriptionSegment[], filename: string = 'subtitle'): void {
  const content = generateSRT(segments);
  downloadSubtitle(content, filename, 'srt');
}

/**
 * 快捷方法：生成並下載 VTT 字幕
 */
export function downloadVTT(segments: TranscriptionSegment[], filename: string = 'subtitle'): void {
  const content = generateVTT(segments);
  downloadSubtitle(content, filename, 'vtt');
}
